import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { fivemApi } from '../../api/fivem.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import type { FiveMServer } from '../../api/types'

export function FiveMServerFormModal({ server, onClose }: { server?: FiveMServer | null; onClose: () => void }) {
  const isEdit = Boolean(server)
  const queryClient = useQueryClient()

  const [name, setName] = useState(server?.name ?? '')
  const [host, setHost] = useState(server?.host ?? '')
  const [port, setPort] = useState(server?.port ?? 30120)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const saveMutation = useMutation({
    mutationFn: () =>
      isEdit && server
        ? fivemApi.updateServer(server.id, { name, host, port })
        : fivemApi.createServer({ name, host, port }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fivem', 'servers'] })
      toast.success(isEdit ? 'Server aktualisiert.' : 'Server angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => fivemApi.removeServer(server!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fivem', 'servers'] })
      toast.success('Server entfernt.')
      onClose()
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    saveMutation.mutate()
  }

  return (
    <Modal
      title={isEdit ? 'FiveM-Server bearbeiten' : 'FiveM-Server hinzufügen'}
      onClose={onClose}
      footer={
        <>
          {isEdit ? (
            <Button variant="danger" onClick={() => setConfirmDelete(true)} style={{ marginRight: 'auto' }}>
              Entfernen
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={saveMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}
        <Field label="Name" htmlFor="fm-name" hint="Anzeigename, z.B. Live-Server oder Test-Server">
          <Input id="fm-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <Field
          label="IP / Hostname"
          htmlFor="fm-host"
          hint="Läuft dieser FiveM-Server auf demselben Pterodactyl-Node wie dieses Dashboard, blockiert Wings' Container-Isolation (ICC) standardmäßig die Statusabfrage — er zeigt dann fälschlich 'Offline', obwohl er läuft. Betrifft nur die Anzeige hier, nicht die Erreichbarkeit für Spieler."
        >
          <Input id="fm-host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="z.B. 194.15.38.14" required />
        </Field>
        <Field label="Port" htmlFor="fm-port">
          <Input id="fm-port" type="number" min={1} max={65535} value={port} onChange={(e) => setPort(Number(e.target.value))} required />
        </Field>
      </form>

      {confirmDelete ? (
        <ConfirmDialog
          title="Server entfernen"
          message={`"${server?.name}" wirklich aus der Übersicht entfernen?`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Modal>
  )
}
